import { z } from 'zod';
/**
 * Machine credential contracts - API keys a workspace admin generates so an
 * unattended external client (the MCP server, CI, a nightly automation) can call
 * the API without a browser session.
 *
 * Shared by apps/api (route validation) and apps/web (the admin UI) so the two
 * cannot drift.
 */
/**
 * Modules a credential may be granted access to.
 *
 * Kept as an explicit allowlist rather than a free string: this is the actual
 * boundary on what a leaked key can reach, so a typo must be a validation error
 * rather than a silently ungranted module.
 *
 * MUST match the module key the target routes gate on. The MCP tools call `/sales/*`,
 * and those routes resolve access with `resolveModuleAccess(auth, 'sales')` - a
 * credential granted only `product` authenticates fine and then fails every call with
 * MODULE_ACCESS_DENIED, which reads like a broken key rather than a missing grant.
 */
export const MACHINE_CREDENTIAL_MODULE_KEYS = ['sales', 'product'];
/**
 * What a credential is granted when the caller does not say.
 *
 * `sales` because that is what the MCP estimation tools need.
 */
export const DEFAULT_MACHINE_CREDENTIAL_MODULE_KEYS = ['sales'];
/**
 * The single module role every credential's system user is provisioned with.
 *
 * There used to be a per-key access level (own-deals `collaborator` vs workspace-wide
 * `admin`). It was removed on purpose: every real consumer (the MCP server, scheduled
 * automations) operates on the whole workspace, and the collaborator default made a
 * freshly provisioned UAT/PROD key look broken - it authenticated fine and then saw
 * none of the workspace's deals. Every key is workspace-level now; the blast-radius
 * control is the key lifecycle itself (expiry, revocation, per-client keys), not a
 * per-key role. Note this is the MODULE role - the principal is still never a
 * workspace admin, so a leaked key cannot mint or revoke keys.
 */
export const MACHINE_CREDENTIAL_ROLE = 'admin';
/**
 * Validity applied when a create request does not name an expiry.
 *
 * One year balances "unattended clients should not break mid-quarter" against
 * "a forgotten key should not stay live forever". The expiry-notice sweep emails
 * the creator and the ops recipients at 14 days, 7 days, and on expiry, so the
 * key never lapses silently.
 */
export const DEFAULT_MACHINE_CREDENTIAL_VALIDITY_DAYS = 365;
export const createMachineCredentialRequestSchema = z.object({
    /** Admin-supplied label, e.g. "Houseblend nightly board sync". */
    name: z.string().trim().min(1).max(120),
    moduleKeys: z.array(z.enum(MACHINE_CREDENTIAL_MODULE_KEYS)).min(1),
    /**
     * Hard expiry, ISO 8601. Omitted means the server applies the default validity
     * (DEFAULT_MACHINE_CREDENTIAL_VALIDITY_DAYS) - keys no longer live forever.
     * Pass an explicit date to shorten or lengthen a specific key's life.
     */
    expiresAt: z.string().datetime().optional().nullable(),
});
//# sourceMappingURL=machine-credential.js.map